import { createSignal } from 'solid-js';
import { resetSelectionForTests } from './selection';

/** The identity of one server across frames — NOT the bare port, which two
 *  servers on different interfaces can share. */
export function serverKeyOf(host: string, port: number): string {
  return `${host}:${port}`;
}

export interface CpuPoint {
  ts: number;
  usagePct: number;
  load1m: number;
  load5m: number;
  load15m: number;
  /** Host identity, added in agent v0.14.0 — null when an older agent is paired. */
  threads: number | null;
  uptimeS: number | null;
  kernel: string | null;
}

export interface MemPoint {
  ts: number;
  totalMb: number;
  usedMb: number;
  availableMb: number;
  swapTotalMb: number;
  swapUsedMb: number;
  swapInPerS: number | null;
  swapOutPerS: number | null;
  diskTotalMb: number | null;
  diskFreeMb: number | null;
}

export interface GpuProcessView {
  pid: number;
  name: string;
  usedVramMb: number | null;
  kind: 'compute' | 'graphics' | 'unknown';
}

export interface GpuDeviceView {
  vendor: 'nvidia' | 'amd' | 'intel';
  index: number;
  name: string;
  utilPct: number | null;
  vramTotalMb: number | null;
  vramUsedMb: number | null;
  vramShared: boolean;
  tempC: number | null;
  powerW: number | null;
  powerLimitW: number | null;
  clockCoreMhz: number | null;
  clockMemMhz: number | null;
  memUtilPct: number | null;
  clockCoreMaxMhz: number | null;
  tempSlowdownC: number | null;
  fanPct: number | null;
  throttleReasons: string[];
  driverVersion: string | null;
  processes: GpuProcessView[];
}

export interface GpuSourceView {
  vendor: 'nvidia' | 'amd' | 'intel';
  status: 'available' | 'absent' | 'unavailable';
  detail?: string;
  reason?: string;
  hint?: string;
  gaps?: Array<{ metric: string; reason: string; hint: string }>;
}

export interface GpuSnapshot {
  ts: number;
  sources: GpuSourceView[];
  devices: GpuDeviceView[];
}

export interface LoadedModelView {
  name: string;
  vramMb: number;
  sizeMb: number;
  expiresAt: string | null;
}

export interface OllamaSnapshot {
  reachable: boolean;
  version?: string;
  models: Array<{ name: string; sizeMb: number }>;
  loaded: LoadedModelView[];
}

export interface RouterModelView {
  id: string;
  loaded: boolean;
  active: boolean;
  sizeMb: number | null;
}

/**
 * One llama.cpp server, carrying every fact the runner card renders per server.
 * Not a subset on purpose: a card that takes the model name from the selected tab
 * but the quant, tools, modalities and sleeping badge from the primary describes
 * two servers at once under a tab strip that promises one — and the
 * sleeping/placement mix prints an outright falsehood ("partly on CPU" about a
 * model that is not loaded). Every field here is already on the wire: an entry in
 * `servers[]` is a whole flattened `LlamacppStatus` plus its address.
 */
export type LlamacppServerView = {
  port: number;
  host: string;
  /** The process listening at this address. In router mode this is the
   *  dispatcher, not the process holding the model — that is `serverPid`. */
  pid: number | null;
  fromConfig: boolean;
  detected: boolean;
  model: string | null;
  modelSizeMb: number | null;
  /** The PID the agent resolved for the process actually holding the model: the
   *  router's child in router mode, the listener itself otherwise. */
  serverPid: number | null;
  version: string | null;
  quant: string | null;
  supportsTools: boolean | null;
  supportsParallelTools: boolean | null;
  isSleeping: boolean | null;
  modalities: string[] | null;
  metricsEnabled: boolean;
  router: boolean | null;
  modelsLoaded: number | null;
  modelsMax: number | null;
  models: RouterModelView[] | null;
  /** Catalogue entries the agent cut from `models` to fit the wire; `null` = it sent no
   *  count (nothing cut, or an agent before 0.21.8). Never a guess from the list length. */
  modelsOmitted: number | null;
  /** This poll actually carried a `/metrics` body for THIS server. */
  metricsSampled: boolean;
  tokensPerSec: number | null;
  tokensPerSecLive: number | null;
  promptTokensPerSec: number | null;
  requestsProcessing: number | null;
  requestsDeferred: number | null;
  ctxUsed: number | null;
  ctxLimit: number | null;
  ctxSource: string | null;
  avgTokensPerSec: number | null;
  avgPromptTokensPerSec: number | null;
  promptTokensTotal: number | null;
  tokensPredictedTotal: number | null;
  totalSlots: number | null;
};

export interface InventoryRootView {
  path: string;
  exists: boolean;
  /** `false` is a permissions problem, shown as such — never as "0 models". */
  readable: boolean;
  modelCount: number;
  /** The walk hit its ceiling: `modelCount` is a floor. */
  truncated: boolean;
  /** Unix seconds a RUNNING server last named this directory; null if unknown. */
  lastAlive: number | null;
}

export interface InventoryModelView {
  id: string;
  name: string | null;
  sizeMb: number | null;
  /** `null` = no running server vouched for this file. Never invented. */
  loaded: boolean | null;
  root: string;
  path: string;
}

export interface LlamacppInventoryView {
  /** Unix seconds the directories were last read. */
  observedAt: number;
  roots: InventoryRootView[];
  models: InventoryModelView[];
  /** Models the agent found before it cut `models` to fit the wire. `null` on an agent
   *  before 0.21.8, which sent the whole list — then `models.length` is the count. */
  total: number | null;
}

export interface LlamacppSnapshot {
  detected: boolean;
  /** The server was started with `--metrics` (from `/props`, which never blocks). */
  metricsEnabled: boolean;
  /**
   * This poll actually carried a `/metrics` body. False with `metricsEnabled: true`
   * means the inference loop is mid-batch and hasn't answered yet — hold the last
   * numbers, don't accuse the server of running without the flag.
   */
  metricsSampled: boolean;
  version: string | null;
  model: string | null;
  tokensPerSec: number | null;
  tokensPerSecLive: number | null;
  promptTokensPerSec: number | null;
  requestsProcessing: number | null;
  requestsDeferred: number | null;
  ctxUsed: number | null;
  ctxLimit: number | null;
  ctxSource: string | null;
  modelSizeMb: number | null;
  serverPid: number | null;
  quant: string | null;
  supportsTools: boolean | null;
  supportsParallelTools: boolean | null;
  avgTokensPerSec: number | null;
  avgPromptTokensPerSec: number | null;
  isSleeping: boolean | null;
  promptTokensTotal: number | null;
  tokensPredictedTotal: number | null;
  totalSlots: number | null;
  modalities: string[] | null;
  router: boolean | null;
  modelsLoaded: number | null;
  modelsMax: number | null;
  models: RouterModelView[] | null;
  /** As on `LlamacppServerView`: catalogue entries cut from `models`; `null` = no count sent. */
  modelsOmitted: number | null;
  /**
   * `null` means the agent sent no `servers` key — never "no servers". Two live
   * builds produce it: one that predates discovery, and a current one that found
   * nothing (`assemble_frame(vec![])` omits the key), which still says so through
   * `detected: false`. Either way the card must keep rendering from the flat
   * fields rather than showing zero servers.
   */
  servers: LlamacppServerView[] | null;
  /** Whether this agent's platform can auto-detect servers at all (e.g. no `/proc` on macOS). */
  discoverySupported: boolean | null;
  /** `null` means the agent sent no `inventory` key (a build before 0.17.0). */
  inventory: LlamacppInventoryView | null;
  stale: boolean;
}

export interface TpsPoint {
  ts: number;
  tps: number;
}

export const BUFFER_SECS = 60;

const [cpuSig, setCpu] = createSignal<CpuPoint[]>([]);
const [memSig, setMem] = createSignal<MemPoint[]>([]);
const [gpuSig, setGpu] = createSignal<GpuSnapshot | null>(null);
const [ollamaSig, setOllama] = createSignal<OllamaSnapshot | null>(null);
const [llamacppSig, setLlamacpp] = createSignal<LlamacppSnapshot | null>(null);
const [llamacppTpsSig, setLlamacppTps] = createSignal<TpsPoint[]>([]);
const [tpsByServerSig, setTpsByServer] = createSignal<Record<string, TpsPoint[]>>({});

export interface PauseView {
  paused: boolean;
  since: number | null;
}
const [pauseSig, setPause] = createSignal<PauseView>({ paused: false, since: null });

const LLAMACPP_GRACE = 3;
let llamacppMissCount = 0;
let llamacppMetricsMissCount = 0;
/** Last SAMPLED entry per server key, for `holdServersThroughMetricsGap`. */
let heldServers: Record<string, LlamacppServerView> = {};

export const cpu = cpuSig;
export const mem = memSig;
export const gpu = gpuSig;
export const ollama = ollamaSig;
export const llamacpp = llamacppSig;
export const llamacppTps = llamacppTpsSig;
export const llamacppTpsByServer = tpsByServerSig;
export const pause = pauseSig;

interface CpuFrame { type: 'cpu'; ts: number; usage_pct: number; load_1m: number; load_5m: number; load_15m: number; threads?: number; uptime_s?: number; kernel?: string | null }
interface MemFrame { type: 'mem'; ts: number; total_mb: number; used_mb: number; available_mb: number; swap_total_mb: number; swap_used_mb: number; swap_in_per_s: number | null; swap_out_per_s: number | null; disk_total_mb: number | null; disk_free_mb: number | null }
interface GpuFrameDevice {
  vendor: 'nvidia' | 'amd' | 'intel'; index: number; name: string;
  util_pct: number | null; vram_total_mb: number | null; vram_used_mb: number | null;
  vram_shared: boolean; temp_c: number | null; power_w: number | null;
  power_limit_w: number | null; clock_core_mhz: number | null; clock_mem_mhz: number | null;
  fan_pct: number | null; mem_util_pct: number | null; clock_core_max_mhz: number | null; temp_slowdown_c: number | null; enc_util_pct: number | null; dec_util_pct: number | null;
  throttle_reasons: string[]; driver_version: string | null;
  processes: Array<{ pid: number; name: string; used_vram_mb: number | null; kind: string }>;
}
interface GpuFrame {
  type: 'gpu'; ts: number;
  sources: Array<{ vendor: string; status: string; detail?: string; reason?: string; hint?: string; gaps?: Array<{ metric: string; reason: string; hint: string }> }>;
  devices: GpuFrameDevice[];
}
interface OllamaFrame { type: 'ollama'; ts: number; reachable: boolean; version?: string; catalog: Array<{ name: string; size_mb: number }>; loaded: Array<{ name: string; vram_mb: number; size_mb: number; expires_at: string | null }> }
interface LlamacppFrame {
  type: 'llamacpp'; ts: number;
  detected: boolean; metrics_enabled: boolean;
  /** Absent on agents before 0.12.1 — falls back to `metrics_enabled`. */
  metrics_sampled?: boolean;
  version: string | null; model: string | null;
  tokens_per_sec: number | null; tokens_per_sec_live: number | null; prompt_tokens_per_sec: number | null;
  requests_processing: number | null; requests_deferred: number | null;
  ctx_used: number | null; ctx_limit: number | null; ctx_source: string | null;
  model_size_mb: number | null;
  server_pid: number | null;
  quant?: string | null;
  supports_tools?: boolean | null;
  supports_parallel_tools?: boolean | null;
  avg_tokens_per_sec?: number | null;
  avg_prompt_tokens_per_sec?: number | null;
  is_sleeping?: boolean | null;
  prompt_tokens_total?: number | null;
  tokens_predicted_total?: number | null;
  total_slots?: number | null;
  modalities?: string[] | null;
  router?: boolean | null;
  models_loaded?: number | null;
  models_max?: number | null;
  models?: Array<{ id: string; loaded: boolean; active: boolean; size_mb: number | null }> | null;
  /** Catalogue entries the agent cut from `models`; sent only when > 0 (agents ≥ 0.21.8). */
  models_omitted?: number | null;
  /** Each entry is a whole flattened `LlamacppStatus` plus its address, so every
   *  field the flat frame carries is available per server. */
  servers?: Array<{
    port: number; host: string; pid?: number | null; from_config?: boolean;
    detected?: boolean; model?: string | null; model_size_mb?: number | null;
    server_pid?: number | null; version?: string | null; quant?: string | null;
    supports_tools?: boolean | null; supports_parallel_tools?: boolean | null;
    is_sleeping?: boolean | null; modalities?: string[] | null;
    metrics_enabled?: boolean;
    router?: boolean | null; models_loaded?: number | null; models_max?: number | null;
    models?: Array<{ id: string; loaded: boolean; active: boolean; size_mb: number | null }> | null;
    models_omitted?: number | null;
    metrics_sampled?: boolean;
    tokens_per_sec?: number | null;
    tokens_per_sec_live?: number | null;
    prompt_tokens_per_sec?: number | null;
    requests_processing?: number | null;
    requests_deferred?: number | null;
    ctx_used?: number | null;
    ctx_limit?: number | null;
    ctx_source?: string | null;
    avg_tokens_per_sec?: number | null;
    avg_prompt_tokens_per_sec?: number | null;
    prompt_tokens_total?: number | null;
    tokens_predicted_total?: number | null;
    total_slots?: number | null;
  }> | null;
  /** Absent/null on an agent that predates server discovery — that's "unknown", not "false". */
  discovery_supported?: boolean | null;
  /** Absent on agents before 0.17.0 — "unknown", not "no models". */
  inventory?: {
    observed_at: number;
    /** Models found before the agent cut `models`; absent on agents before 0.21.8. */
    total?: number;
    roots: Array<{ path: string; exists: boolean; readable: boolean; model_count: number; truncated: boolean; last_alive?: number | null }>;
    models: Array<{ id: string; name: string | null; size_mb: number | null; loaded: boolean | null; root: string; path: string }>;
  } | null;
}

/**
 * `null` means the agent sent no `servers` key — never "no servers". Both an
 * agent that predates discovery and a current agent that found nothing send that
 * shape, since `assemble_frame(vec![])` omits the key; the two are told apart by
 * `detected` and `discoverySupported`, not by this field.
 */
export function mapLlamacppServers(
  raw: LlamacppFrame['servers'],
): LlamacppServerView[] | null {
  if (raw == null) return null;
  return raw.map((s) => ({
    port: s.port,
    host: s.host,
    pid: s.pid ?? null,
    fromConfig: s.from_config ?? false,
    detected: s.detected ?? false,
    model: s.model ?? null,
    modelSizeMb: s.model_size_mb ?? null,
    serverPid: s.server_pid ?? null,
    version: s.version ?? null,
    quant: s.quant ?? null,
    supportsTools: s.supports_tools ?? null,
    supportsParallelTools: s.supports_parallel_tools ?? null,
    isSleeping: s.is_sleeping ?? null,
    modalities: s.modalities ?? null,
    metricsEnabled: s.metrics_enabled ?? false,
    router: s.router ?? null,
    modelsLoaded: s.models_loaded ?? null,
    modelsMax: s.models_max ?? null,
    models:
      s.models?.map((m) => ({
        id: m.id,
        loaded: m.loaded,
        active: m.active,
        sizeMb: m.size_mb ?? null,
      })) ?? null,
    modelsOmitted: s.models_omitted ?? null,
    metricsSampled: s.metrics_sampled ?? s.metrics_enabled ?? false,
    tokensPerSec: s.tokens_per_sec ?? null,
    tokensPerSecLive: s.tokens_per_sec_live ?? null,
    promptTokensPerSec: s.prompt_tokens_per_sec ?? null,
    requestsProcessing: s.requests_processing ?? null,
    requestsDeferred: s.requests_deferred ?? null,
    ctxUsed: s.ctx_used ?? null,
    ctxLimit: s.ctx_limit ?? null,
    ctxSource: s.ctx_source ?? null,
    avgTokensPerSec: s.avg_tokens_per_sec ?? null,
    avgPromptTokensPerSec: s.avg_prompt_tokens_per_sec ?? null,
    promptTokensTotal: s.prompt_tokens_total ?? null,
    tokensPredictedTotal: s.tokens_predicted_total ?? null,
    totalSlots: s.total_slots ?? null,
  }));
}

/** `null` means the agent sent no `inventory` key (a build before 0.17.0). */
export function mapLlamacppInventory(raw: LlamacppFrame['inventory']): LlamacppInventoryView | null {
  if (raw == null) return null;
  return {
    observedAt: raw.observed_at,
    roots: raw.roots.map((r) => ({
      path: r.path, exists: r.exists, readable: r.readable, modelCount: r.model_count,
      truncated: r.truncated, lastAlive: r.last_alive ?? null,
    })),
    models: raw.models.map((m) => ({
      id: m.id, name: m.name ?? null, sizeMb: m.size_mb ?? null, loaded: m.loaded ?? null, root: m.root, path: m.path,
    })),
    total: raw.total ?? null,
  };
}

/**
 * One tick of the per-server sparkline buffers. A server buffers a point only when
 * THIS poll carried a `/metrics` sample with a live rate — the same rule the flat
 * buffer follows — and a server missing from this frame loses its history: its
 * key would otherwise outlive the tab it belonged to.
 */
export function foldServerTps(
  prev: Record<string, TpsPoint[]>,
  raw: LlamacppFrame['servers'],
  ts: number,
): Record<string, TpsPoint[]> {
  const next: Record<string, TpsPoint[]> = {};
  for (const s of raw ?? []) {
    const key = serverKeyOf(s.host, s.port);
    const had = prev[key];
    const sampled = s.metrics_sampled ?? s.metrics_enabled ?? false;
    if (sampled && s.tokens_per_sec_live != null) {
      next[key] = trimByTs([...(had ?? []), { ts, tps: s.tokens_per_sec_live }], BUFFER_SECS);
    } else if (had) {
      // No fresh point this poll, but keep the history it already had.
      next[key] = had;
    }
    // Neither condition: this server has no history and contributed nothing —
    // don't manufacture a key for it.
  }
  return next;
}

/** The fields `/metrics` supplies — the ones a starved poll leaves null and a hold carries over. */
const SERVER_METRIC_FIELDS = [
  'tokensPerSec', 'tokensPerSecLive', 'promptTokensPerSec', 'requestsProcessing', 'requestsDeferred',
  'ctxUsed', 'ctxSource', 'avgTokensPerSec', 'avgPromptTokensPerSec', 'promptTokensTotal',
  'tokensPredictedTotal',
] as const;

type ServerMetricField = (typeof SERVER_METRIC_FIELDS)[number];
type ServerMetrics = Pick<LlamacppServerView, ServerMetricField>;

/** Copy one remembered field. Generic so the key narrows both sides at once: the
 *  listed fields are not all the same type (`ctxSource` is a string), which a
 *  non-generic index assignment cannot express. */
function carryOver<K extends ServerMetricField>(
  into: Partial<ServerMetrics>,
  from: LlamacppServerView,
  key: K,
): void {
  into[key] = from[key];
}

/**
 * Per-server twin of `heldThroughMetricsGap`: for a server whose poll carried no
 * `/metrics` sample (`metricsSampled === false` with `metricsEnabled`), keep the LAST
 * SAMPLED entry's metric numbers and take every `/props`-derived fact from this poll;
 * a sampled entry replaces the remembered one; servers absent from this frame are
 * forgotten. Returns the servers to store and the new memory. `null` servers pass
 * through untouched — the agent sent no `servers` key, which says nothing about any
 * server, so the memory is left standing.
 *
 * Without this a machine running two servers watches Speed/Context/Queue/Processed
 * blink to `—` for the length of a prefill, while a single-server machine holds them:
 * `/metrics` is answered by the inference loop between batches, so a long prompt
 * starves it for minutes. `ctxLimit`, `totalSlots`, `model`, `quant`, `isSleeping`,
 * `router` and `models` are `/props`/catalog facts and stay FRESH — the same split
 * the flat hold makes.
 */
export function holdServersThroughMetricsGap(
  remembered: Record<string, LlamacppServerView>,
  fresh: LlamacppServerView[] | null,
): { servers: LlamacppServerView[] | null; remembered: Record<string, LlamacppServerView> } {
  if (fresh == null) return { servers: null, remembered };
  const next: Record<string, LlamacppServerView> = {};
  const servers = fresh.map((s) => {
    const key = serverKeyOf(s.host, s.port);
    if (s.metricsSampled) {
      next[key] = s;
      return s;
    }
    // A server without `--metrics` has nothing to hold: no memory is kept for it,
    // and its nulls are the honest answer rather than a stale number.
    const held = s.metricsEnabled ? remembered[key] : undefined;
    if (!held) return s;
    next[key] = held;
    const metrics: Partial<ServerMetrics> = {};
    for (const field of SERVER_METRIC_FIELDS) carryOver(metrics, held, field);
    return { ...s, ...metrics };
  });
  return { servers, remembered: next };
}

interface TelemetryStateFrame { type: 'telemetry_state'; paused: boolean; since: number | null; ts: number }

/** Belt and braces: a data frame can only mean the pollers are running, so it
 * clears a pause even if the resume `telemetry_state` frame was lost. */
function clearPauseOnData(): void {
  if (pauseSig().paused) setPause({ paused: false, since: null });
}

/**
 * Accepts the post-JSON.parse output from the decrypt loop. `unknown` because the bytes are
 * untrusted until shape-checked — this function performs the narrowing.
 */
export function dispatchFrame(frame: unknown): void {
  if (!frame || typeof frame !== 'object' || !('type' in frame)) {
    console.warn('non-object or untyped frame', frame);
    return;
  }
  const t = (frame as { type: unknown }).type;
  if (typeof t !== 'string') {
    console.warn('frame type is not a string', frame);
    return;
  }
  switch (t) {
    case 'hello':
      // A new session starts clean; the agent's post-hello telemetry_state
      // frame re-asserts a pause within the same tick if there is one.
      setPause({ paused: false, since: null });
      return;
    case 'pong':
    case 'agent_stopping':
      return;
    case 'error':
      console.warn('agent error frame', frame);
      return;
    case 'telemetry_state': {
      const f = frame as unknown as TelemetryStateFrame;
      setPause({ paused: !!f.paused, since: f.since ?? null });
      return;
    }
    case 'cpu': {
      clearPauseOnData();
      const f = frame as unknown as CpuFrame;
      setCpu((prev) =>
        trimByTs(
          [...prev, {
            ts: f.ts,
            usagePct: f.usage_pct,
            load1m: f.load_1m,
            load5m: f.load_5m,
            load15m: f.load_15m,
            threads: f.threads ?? null,
            uptimeS: f.uptime_s ?? null,
            kernel: f.kernel ?? null,
          }],
          BUFFER_SECS,
        ),
      );
      return;
    }
    case 'mem': {
      clearPauseOnData();
      const f = frame as unknown as MemFrame;
      setMem((prev) =>
        trimByTs(
          [...prev, { ts: f.ts, totalMb: f.total_mb, usedMb: f.used_mb, availableMb: f.available_mb, swapTotalMb: f.swap_total_mb, swapUsedMb: f.swap_used_mb, swapInPerS: f.swap_in_per_s, swapOutPerS: f.swap_out_per_s, diskTotalMb: f.disk_total_mb, diskFreeMb: f.disk_free_mb }],
          BUFFER_SECS,
        ),
      );
      return;
    }
    case 'gpu': {
      clearPauseOnData();
      const f = frame as unknown as GpuFrame;
      // Contract: do not defensively shape-check; a bad frame throws (caught upstream).
      setGpu({
        ts: f.ts,
        sources: f.sources.map((s) => ({
          vendor: s.vendor as GpuSourceView['vendor'],
          status: s.status as GpuSourceView['status'],
          detail: s.detail, reason: s.reason, hint: s.hint, gaps: s.gaps,
        })),
        devices: f.devices.map((d) => ({
          vendor: d.vendor, index: d.index, name: d.name,
          utilPct: d.util_pct, vramTotalMb: d.vram_total_mb, vramUsedMb: d.vram_used_mb,
          vramShared: d.vram_shared, tempC: d.temp_c, powerW: d.power_w,
          powerLimitW: d.power_limit_w, clockCoreMhz: d.clock_core_mhz, clockMemMhz: d.clock_mem_mhz,
          memUtilPct: d.mem_util_pct, clockCoreMaxMhz: d.clock_core_max_mhz, tempSlowdownC: d.temp_slowdown_c,
          fanPct: d.fan_pct, throttleReasons: d.throttle_reasons, driverVersion: d.driver_version,
          processes: d.processes.map((p) => ({
            pid: p.pid, name: p.name, usedVramMb: p.used_vram_mb,
            kind: (['compute', 'graphics'].includes(p.kind) ? p.kind : 'unknown') as GpuProcessView['kind'],
          })),
        })),
      });
      return;
    }
    case 'ollama': {
      clearPauseOnData();
      const f = frame as unknown as OllamaFrame;
      setOllama({
        reachable: f.reachable,
        version: f.version,
        models: f.catalog.map((m) => ({ name: m.name, sizeMb: m.size_mb })),
        loaded: f.loaded.map((m) => ({ name: m.name, vramMb: m.vram_mb, sizeMb: m.size_mb, expiresAt: m.expires_at })),
      });
      return;
    }
    case 'llamacpp': {
      clearPauseOnData();
      const f = frame as unknown as LlamacppFrame;
      // Agents before 0.12.1 derived metrics_enabled from the sample itself, so the
      // two facts coincide there — that keeps the legacy grace below in charge.
      const sampled = f.metrics_sampled ?? f.metrics_enabled;
      const mapped: LlamacppSnapshot = {
        detected: f.detected,
        metricsEnabled: f.metrics_enabled,
        metricsSampled: sampled,
        version: f.version,
        model: f.model,
        tokensPerSec: f.tokens_per_sec,
        tokensPerSecLive: f.tokens_per_sec_live ?? null,
        promptTokensPerSec: f.prompt_tokens_per_sec,
        requestsProcessing: f.requests_processing,
        requestsDeferred: f.requests_deferred,
        ctxUsed: f.ctx_used,
        ctxLimit: f.ctx_limit,
        ctxSource: f.ctx_source,
        modelSizeMb: f.model_size_mb ?? null,
        serverPid: f.server_pid ?? null,
        quant: f.quant ?? null,
        supportsTools: f.supports_tools ?? null,
        supportsParallelTools: f.supports_parallel_tools ?? null,
        avgTokensPerSec: f.avg_tokens_per_sec ?? null,
        avgPromptTokensPerSec: f.avg_prompt_tokens_per_sec ?? null,
        isSleeping: f.is_sleeping ?? null,
        promptTokensTotal: f.prompt_tokens_total ?? null,
        tokensPredictedTotal: f.tokens_predicted_total ?? null,
        totalSlots: f.total_slots ?? null,
        modalities: f.modalities ?? null,
        router: f.router ?? null,
        modelsLoaded: f.models_loaded ?? null,
        modelsMax: f.models_max ?? null,
        models:
          f.models?.map((m) => ({
            id: m.id,
            loaded: m.loaded,
            active: m.active,
            sizeMb: m.size_mb ?? null,
          })) ?? null,
        modelsOmitted: f.models_omitted ?? null,
        servers: mapLlamacppServers(f.servers),
        discoverySupported: f.discovery_supported ?? null,
        inventory: mapLlamacppInventory(f.inventory),
        stale: false,
      };
      // Per-server tok/s history is a fact of THIS poll regardless of how the flat
      // snapshot below branches (fresh/held/stale) — a server's sparkline shouldn't
      // freeze just because the primary server's /metrics sample missed a beat.
      setTpsByServer((prev) => foldServerTps(prev, f.servers, f.ts));
      // Held BEFORE the branches below, so every one of them (sampled / gap / grace /
      // undetected) stores servers whose numbers survived a starved /metrics poll —
      // the flat fields already do, and with two servers the hero reads these.
      const heldFrame = holdServersThroughMetricsGap(heldServers, mapped.servers);
      heldServers = heldFrame.remembered;
      mapped.servers = heldFrame.servers;
      // The router unloading its only model is a proven EVENT, not a poll miss: the
      // router itself told us `models_loaded: 0, model: null` this very poll. Holding
      // the previous model's numbers here would violate the "honesty of the event"
      // rule — the badge would say "0 of M loaded" while Speed/Processed/Context kept
      // showing a model that is provably gone. Holds exist only for a /metrics sample
      // that didn't arrive for a model that (as far as we know) is still there.
      const routerHasNoModel = mapped.router === true && mapped.model == null;
      if (f.detected && f.metrics_enabled && sampled) {
        llamacppMissCount = 0;
        llamacppMetricsMissCount = 0;
        setLlamacpp(mapped);
        // Sparkline plots the LIVE decode rate; buffered whenever a live number
        // is present (idle-live 0 included).
        if (f.tokens_per_sec_live != null) {
          setLlamacppTps((prev) => trimByTs([...prev, { ts: f.ts, tps: f.tokens_per_sec_live as number }], BUFFER_SECS));
        }
      } else if (f.detected && f.metrics_enabled && !routerHasNoModel) {
        // /props says `--metrics` IS on, this poll just didn't carry a sample:
        // `/metrics` is answered by the inference loop between batches, so a long
        // prompt-processing run starves it for as long as the prefill lasts —
        // minutes on a big cold context. No grace counter here: the flag is a fact
        // from /props, so there is nothing to expire into. Hold the numbers, mark
        // them stale, and let the fresh /props facts through.
        llamacppMissCount = 0;
        llamacppMetricsMissCount = 0;
        const prev = llamacppSig();
        setLlamacpp(prev?.detected ? heldThroughMetricsGap(prev, mapped) : { ...mapped, stale: true });
      } else if (f.detected) {
        // Pre-0.12.1 agent: `metrics_enabled` was derived from the sample, so a
        // timeout is indistinguishable from a server without the flag. Hold the
        // last good numbers for a few polls rather than flashing the hint at a
        // server that has `--metrics` on.
        llamacppMissCount = 0;
        llamacppMetricsMissCount += 1;
        const prev = llamacppSig();
        if (!routerHasNoModel && llamacppMetricsMissCount <= LLAMACPP_GRACE && prev?.detected && prev?.metricsEnabled) {
          setLlamacpp(heldThroughMetricsGap(prev, mapped));
        } else {
          // Genuinely running without --metrics (or the grace ran out): be honest.
          setLlamacpp(mapped);
        }
      } else {
        // Deliberately do NOT reset llamacppMetricsMissCount here: an undetected
        // frame tells us nothing about whether /metrics would have answered, so
        // resetting it would let an alternating undetected/metrics-off sequence
        // keep both counters at <=1 forever and hold a stale snapshot indefinitely.
        llamacppMissCount += 1;
        const prev = llamacppSig();
        if (llamacppMissCount <= LLAMACPP_GRACE && prev?.detected) {
          // Short miss under GPU saturation: hold the last good frame, flag stale.
          setLlamacpp({ ...prev, stale: true });
        } else {
          // Server is really gone (or never seen): release honestly.
          setLlamacpp(mapped);
        }
      }
      return;
    }
    default:
      console.warn('unknown frame type', t);
  }
}

/**
 * Snapshot for a poll where `/props` answered but `/metrics` didn't: keep the last
 * poll's `/metrics`-derived numbers, take every `/props`- and catalog-derived fact
 * from THIS poll. Replaying the previous poll's `/props` data would show a stale
 * isSleeping/model/quant/router count — e.g. a model that unloaded mid-stall would
 * still read as loaded. Both callers have already established that `--metrics` is
 * on (this poll's `/props`, or the last poll that carried a sample).
 */
function heldThroughMetricsGap(prev: LlamacppSnapshot, fresh: LlamacppSnapshot): LlamacppSnapshot {
  return {
    ...prev,
    version: fresh.version,
    model: fresh.model,
    ctxLimit: fresh.ctxLimit,
    modelSizeMb: fresh.modelSizeMb,
    serverPid: fresh.serverPid,
    quant: fresh.quant,
    supportsTools: fresh.supportsTools,
    supportsParallelTools: fresh.supportsParallelTools,
    isSleeping: fresh.isSleeping,
    totalSlots: fresh.totalSlots,
    modalities: fresh.modalities,
    router: fresh.router,
    modelsLoaded: fresh.modelsLoaded,
    modelsMax: fresh.modelsMax,
    models: fresh.models,
    modelsOmitted: fresh.modelsOmitted,
    servers: fresh.servers,
    discoverySupported: fresh.discoverySupported,
    inventory: fresh.inventory,
    metricsEnabled: true,
    metricsSampled: false,
    stale: true,
  };
}

function trimByTs<T extends { ts: number }>(arr: T[], windowSec: number): T[] {
  if (arr.length === 0) return arr;
  const cutoff = arr[arr.length - 1]!.ts - windowSec;
  return arr.filter((p) => p.ts >= cutoff);
}

/** Test-only: reset signals back to empty/null. Not used in production. */
export function resetForTests(): void {
  setCpu([]);
  setMem([]);
  setGpu(null);
  setOllama(null);
  setLlamacpp(null);
  setLlamacppTps([]);
  setTpsByServer({});
  setPause({ paused: false, since: null });
  resetSelectionForTests();
  llamacppMissCount = 0;
  llamacppMetricsMissCount = 0;
  heldServers = {};
}
